import styles from "./ArticleLeadArt.module.scss";

import { ArticleLayout, ArticleLeadCinemagraphFragment } from "types/api";
import ArticleLeadFigure from "components/article/ArticleLeadFigure";
import ArticleLeadImage from "components/article/ArticleLeadImage";
import Cinemagraph from "components/Cinemagraph";
import { gql } from "utils/urql";
import cx from "classnames";

const ArticleLeadCinemagraph = ({
  layout,
  ...props
}: {
  layout: ArticleLayout;
} & ArticleLeadCinemagraphFragment) => {
  const poster =
    layout === ArticleLayout.Twocol
      ? props.verticalPoster
      : props.horizontalPoster;
  return (
    <>
      {props.__typename === "Cinemagraph" && (
        <ArticleLeadFigure
          layout={layout}
          captionText={props.captionText}
          attributionText={props.attributionText}
        >
          <Cinemagraph
            width={poster?.width || props.vidWidth}
            height={poster?.height || props.vidHeight}
            poster={poster || undefined}
            formats={props.formats}
            className={cx(
              styles.video,
              layout === ArticleLayout.Feature && styles.featureMedia
            )}
          />
          {poster && (
            <ArticleLeadImage
              className={styles.fallbackImage}
              layout={layout}
              lazy={true}
              {...poster}
            />
          )}
        </ArticleLeadFigure>
      )}
    </>
  );
};

ArticleLeadCinemagraph.fragment = gql`
  fragment articleLeadCinemagraphPoster on BasicImage {
    url
    width
    height
    srcSet
    altText
  }
  fragment articleLeadCinemagraph on Cinemagraph {
    vidWidth: width
    vidHeight: height
    attributionText
    captionText
    formats {
      encoding
      url
    }
    horizontalPoster: poster(
      width: 1440
      height: 810
      # Sizes based off of default inspector responsive sizes
      # 640 = 320px * 2 (mobile small retina)
      # 750w = 375 * 2 (mobile retina)
      # 850w = 425 * 2 (mobile large retina)
      # 1536w = 768 * 2 (tablet retina)
      # 1920w = (standard lead art large size)
      # 2048w = 1024 * 2 (laptop retina)
      # 2880w = 1440 * 2 (laptop large retina)
      # "3840w" = 1920 * 2 (standard lead art retina)
      sizes: [
        "640w"
        "750w"
        "850w"
        "1536w"
        "1920w"
        "2048w"
        "2880w"
        "3840w"
      ]
    ) {
      ...articleLeadCinemagraphPoster
    }
    verticalPoster: poster(
      width: 648
      height: 810
      # 296w, 592w = calc(100vw - 64px) (fullwidth tall image with 32px margins on each side)
      #   on a 360px wide screen (common mobile device screen width) at 1x & 2x for mobile retina
      # 311w, 622w = calc(100vw - 103px) (fullwidth tall image with side margins of 51 and 52px)
      #   on a 414px wide screen (common mobile device screen width) at 1x & 2x for mobile retina
      # 665w, 1330w = 665px is the widest the tall image can be on any screen, and 2x for mobile retina
      # 960w = the minimum size for the lead image incase 1330w is too big
      sizes: ["296w", "311w", "592w", "622w", "665w", "960w", "1330w"]
    ) {
      ...articleLeadCinemagraphPoster
    }
  }
`;

export default ArticleLeadCinemagraph;
